/**
 * Build step 4 — timesheets with the gate and analysis (ADR-001 §11.6) and the HR holiday calendar
 * (ADR-005 §6), as amended by operator decisions 8, 9, 18 and the acting-as rule (no filing someone's
 * timesheet while acting as them).
 */
import { IdempotencyHeaders, IfMatchHeaders, OptionalIfMatchHeaders } from './common.js';
import { AutomationCsvQuery, AutomationIdParams, AutomationPage, AutomationQuery, CatchUpBody, CatchUpResponse, CreateFeedbackBody, CreateNudgeBody, FeedbackIdParams, FeedbackPage, FileTimesheetBody, ListFeedbackQuery, ManagerEditTimesheetBody, MyTimesheetQuery, Nudge, TimesheetDateParams, TimesheetDay, TimesheetFeedback, TimesheetGateResponse, TimesheetsQuery, TimesheetsResponse, UpdateAutomationBody, UserDateParams, AutomationCandidate, CreateAutomationBody, WorkingDayBody, WorkingDaySetting, } from './timesheets.js';
import { CalendarDayParams, CalendarIdParams, CreateCalendarBody, CreateDayTypeBody, DayTypeKeyParams, ElectBody, ElectionIdParams, HolidayCalendar, HolidayCalendarList, HolidayDay, HolidayDayBody, HolidayDayType, HolidayDayTypeList, ListCalendarsQuery, MyOptionalHolidays, MyOptionalQuery, OptionalAdminList, OptionalElection, PublishResponse, UpdateCalendarBody, UpdateDayTypeBody, UpdateHolidayDayBody, UserIdOnlyParams, WithdrawResponse, WorkingDaysQuery, WorkingDaysResponse, PublishPreview, UserElectionParams, } from './holidays.js';
import { API_BASE, defineRoute } from './route.js';
const SELF_TS = ['timesheet.file', 'timesheet.read.own'];
const MGR_TS = ['timesheet.read.all'];
const HR = ['holidays.manage'];
const OPTIONAL_ADMIN = ['holidays.manage', 'users.write'];
export const step4Routes = {
    // ============================================================ my timesheet
    'me.timesheet': defineRoute({
        id: 'me.timesheet',
        method: 'GET',
        path: `${API_BASE}/me/timesheet`,
        summary: 'My timesheet for a day (default: today)',
        access: { auth: 'session', anyOf: SELF_TS, note: 'Self only (ops members). Others: 403.' },
        query: MyTimesheetQuery,
        success: { status: 200, description: 'The day', schema: TimesheetDay },
        errors: ['no_session', 'forbidden', 'validation_failed'],
        idempotency: 'safe',
        rateLimit: 'default',
    }),
    'me.timesheetFile': defineRoute({
        id: 'me.timesheetFile',
        method: 'PUT',
        path: `${API_BASE}/me/timesheet/{date}`,
        summary: 'File my timesheet for a day (entries must add up to the hours filed)',
        description: '422 when entries do not sum to hours_filed or the date is in the future; 409 when the day is locked; 403 while ' +
            'acting as someone else (a manager edits in their own name instead). Filing after the on-time cut-off (Settings timesheet.on_time_by, default 18:30 IST) that day = late.',
        access: { auth: 'session', anyOf: ['timesheet.file'], note: 'Self only (ops members). Others: 403.' },
        params: TimesheetDateParams,
        headers: OptionalIfMatchHeaders,
        body: FileTimesheetBody,
        success: { status: 200, description: 'The filed day', schema: TimesheetDay },
        preconditionCurrent: TimesheetDay,
        errors: ['no_session', 'forbidden', 'validation_failed', 'conflict', 'precondition_failed'],
        idempotency: 'safe',
        concurrency: 'if-match-optional',
        rateLimit: 'write',
    }),
    'me.timesheetGate': defineRoute({
        id: 'me.timesheetGate',
        method: 'GET',
        path: `${API_BASE}/me/timesheet/gate`,
        summary: 'Am I gated? (previous working day not filed → catch-up first)',
        description: 'Only ops members are ever blocked; the founder and the associate director are never gated.',
        access: { auth: 'session', note: 'Any signed-in user (non-members always get blocked = false).' },
        success: { status: 200, description: 'The gate', schema: TimesheetGateResponse },
        errors: ['no_session'],
        idempotency: 'safe',
        rateLimit: 'default',
    }),
    'me.timesheetCatchUp': defineRoute({
        id: 'me.timesheetCatchUp',
        method: 'POST',
        path: `${API_BASE}/me/timesheet/catch-up`,
        summary: '“Fill {day}’s timesheet to open today” — files the missed day as late and lifts the gate',
        description: 'work_date must be your gating day (422 otherwise). 403 while acting as someone else.',
        access: { auth: 'session', anyOf: ['timesheet.file'], note: 'Self only (ops members).' },
        headers: IdempotencyHeaders,
        body: CatchUpBody,
        success: { status: 200, description: 'Unblocked', schema: CatchUpResponse },
        errors: ['no_session', 'forbidden', 'validation_failed', 'conflict'],
        idempotency: 'key',
        rateLimit: 'write',
    }),
    // ============================================================ manager views
    'timesheets.grid': defineRoute({
        id: 'timesheets.grid',
        method: 'GET',
        path: `${API_BASE}/timesheets`,
        summary: 'Team → Timesheets: people × dates, coloured against expected hours (+ analysis for the founder)',
        description: 'ops_member gets 403 — a member can never see a colleague’s total by any route (operator decision).',
        access: { auth: 'session', anyOf: MGR_TS, note: 'Managers. Others: 403.' },
        query: TimesheetsQuery,
        success: { status: 200, description: 'Grid and analysis', schema: TimesheetsResponse },
        errors: ['no_session', 'forbidden', 'validation_failed'],
        idempotency: 'safe',
        rateLimit: 'default',
    }),
    'timesheets.managerEdit': defineRoute({
        id: 'timesheets.managerEdit',
        method: 'PATCH',
        path: `${API_BASE}/timesheets/{user_id}/{date}`,
        summary: 'A manager files or corrects someone’s day, in the manager’s own name',
        description: 'A missing day becomes late with filed_by = the manager. A note is required. 409 when locked.',
        access: { auth: 'session', anyOf: MGR_TS, deny: 'forbidden', note: 'Managers. Others: 403.' },
        params: UserDateParams,
        headers: OptionalIfMatchHeaders,
        body: ManagerEditTimesheetBody,
        success: { status: 200, description: 'The day', schema: TimesheetDay },
        errors: ['no_session', 'forbidden', 'not_found', 'validation_failed', 'conflict', 'precondition_failed'],
        idempotency: 'safe',
        concurrency: 'if-match-optional',
        rateLimit: 'write',
    }),
    'nudges.create': defineRoute({
        id: 'nudges.create',
        method: 'POST',
        path: `${API_BASE}/nudges`,
        summary: 'Nudge someone (missing timesheet, or a task) — in-app; a repeat increments the count',
        access: { auth: 'session', anyOf: MGR_TS, note: 'Managers. Others: 403.' },
        headers: IdempotencyHeaders,
        body: CreateNudgeBody,
        success: { status: 201, description: 'The nudge (count incremented on repeat)', schema: Nudge },
        errors: ['no_session', 'forbidden', 'not_found', 'validation_failed'],
        idempotency: 'key',
        rateLimit: 'write',
    }),
    // ============================================================ feedback
    'feedback.list': defineRoute({
        id: 'feedback.list',
        method: 'GET',
        path: `${API_BASE}/timesheets/feedback`,
        summary: 'Manager feedback on time use (managers: all; a member: their own)',
        access: { auth: 'session', anyOf: ['timesheet.read.all', 'timesheet.read.own'], note: 'Managers → all; ops_member → own. Others: 403.' },
        query: ListFeedbackQuery,
        success: { status: 200, description: 'A page, newest first', schema: FeedbackPage },
        errors: ['no_session', 'forbidden', 'validation_failed'],
        idempotency: 'safe',
        rateLimit: 'default',
    }),
    'feedback.create': defineRoute({
        id: 'feedback.create',
        method: 'POST',
        path: `${API_BASE}/timesheets/feedback`,
        summary: 'Write feedback for a member (shown on their Today)',
        access: { auth: 'session', anyOf: ['timesheet.feedback.write'], note: 'The founder or the associate director (operator ruling t-739d4666, prototype tsFeedback). Others: 403.' },
        headers: IdempotencyHeaders,
        body: CreateFeedbackBody,
        success: { status: 201, description: 'Saved', schema: TimesheetFeedback },
        errors: ['no_session', 'forbidden', 'not_found', 'validation_failed'],
        idempotency: 'key',
        rateLimit: 'write',
    }),
    'feedback.delete': defineRoute({
        id: 'feedback.delete',
        method: 'DELETE',
        path: `${API_BASE}/timesheets/feedback/{id}`,
        summary: 'Remove a feedback note',
        access: { auth: 'session', anyOf: ['timesheet.feedback.write'], note: 'The founder or the associate director; the removal is audited under who removed it. Others: 404.' },
        params: FeedbackIdParams,
        success: { status: 204, description: 'Removed' },
        errors: ['no_session', 'not_found'],
        idempotency: 'safe',
        rateLimit: 'write',
    }),
    // ============================================================ automation candidates
    'automation.list': defineRoute({
        id: 'automation.list',
        method: 'GET',
        path: `${API_BASE}/timesheets/automation-candidates`,
        summary: 'Tasks that repeat and could be pushed to a sheet or script',
        access: { auth: 'session', anyOf: MGR_TS, note: 'Managers. Others: 403.' },
        query: AutomationQuery,
        success: { status: 200, description: 'A page, most hours first', schema: AutomationPage },
        errors: ['no_session', 'forbidden', 'validation_failed'],
        idempotency: 'safe',
        rateLimit: 'default',
    }),
    'automation.csv': defineRoute({
        id: 'automation.csv',
        method: 'GET',
        path: `${API_BASE}/timesheets/automation-candidates.csv`,
        summary: 'The automation sheet as CSV (UTF-8 with BOM; opens in Excel)',
        description: 'Columns: Task, How often, Hours a week, Done by, Idea to automate it, Size, Status, Added by. Only rows in the sheet.',
        access: { auth: 'session', anyOf: MGR_TS, note: 'Managers. Others: 403.' },
        query: AutomationCsvQuery,
        success: {
            status: 200,
            description: 'text/csv; charset=utf-8',
            headers: { 'Content-Type': 'text/csv; charset=utf-8', 'Content-Disposition': 'attachment; filename="automation-candidates-YYYY-MM.csv"' },
        },
        errors: ['no_session', 'forbidden', 'validation_failed'],
        idempotency: 'safe',
        rateLimit: 'default',
    }),
    'automation.create': defineRoute({
        id: 'automation.create',
        method: 'POST',
        path: `${API_BASE}/timesheets/automation-candidates`,
        summary: 'Add one of your own to the automation sheet',
        description: 'Created in the sheet (in_sheet true) with status Idea and you as “Added by”. A refresh never removes it.',
        access: { auth: 'session', anyOf: MGR_TS, note: 'Managers. Others: 403.' },
        headers: IdempotencyHeaders,
        body: CreateAutomationBody,
        success: { status: 201, description: 'The new row', schema: AutomationCandidate },
        errors: ['no_session', 'forbidden', 'validation_failed'],
        idempotency: 'key',
        rateLimit: 'write',
    }),
    'automation.update': defineRoute({
        id: 'automation.update',
        method: 'POST',
        path: `${API_BASE}/timesheets/automation-candidates/{id}`,
        summary: 'Add to the sheet, set status (Idea → Live), the fix and the size',
        access: { auth: 'session', anyOf: MGR_TS, note: 'Managers. Others: 404.' },
        params: AutomationIdParams,
        body: UpdateAutomationBody,
        success: { status: 200, description: 'Updated row', schema: AutomationCandidate },
        errors: ['no_session', 'not_found', 'validation_failed'],
        idempotency: 'safe',
        rateLimit: 'write',
    }),
    // ============================================================ the working-day setting (decision 9)
    'settings.workingDay': defineRoute({
        id: 'settings.workingDay',
        method: 'GET',
        path: `${API_BASE}/settings/working-day`,
        summary: 'Settings → the hours of a full working day (default 7.5)',
        access: { auth: 'session', anyOf: ['settings.write'], note: 'Managers. Others: 403. Everyone reads the value on GET /me (config).' },
        success: { status: 200, description: 'The setting', schema: WorkingDaySetting, headers: { ETag: 'W/"<config_version>"' } },
        errors: ['no_session', 'forbidden'],
        idempotency: 'safe',
        rateLimit: 'default',
    }),
    'settings.updateWorkingDay': defineRoute({
        id: 'settings.updateWorkingDay',
        method: 'PATCH',
        path: `${API_BASE}/settings/working-day`,
        summary: 'Change the hours of a full working day',
        description: 'If-Match is the config_version last read (any settings change moves it; 412 with the current value). ' +
            'Audited; bumps the settings config_version. Expected hours are worked out when read, so every day — past ones ' +
            'included — is measured against the new figure from now on; a half day is half of it. The same setting is ' +
            '`timesheet.day_hours` in PATCH /settings/thresholds.',
        access: { auth: 'session', anyOf: ['settings.write'], deny: 'forbidden', note: 'Managers (founder, associate director). Others: 403.' },
        headers: IfMatchHeaders,
        body: WorkingDayBody,
        success: { status: 200, description: 'The setting', schema: WorkingDaySetting, headers: { ETag: 'W/"<config_version>"' } },
        errors: ['no_session', 'forbidden', 'validation_failed', 'precondition_failed'],
        idempotency: 'safe',
        concurrency: 'if-match',
        rateLimit: 'write',
    }),
    // ============================================================ HR holiday calendar (ADR-005 §6)
    'holidays.list': defineRoute({
        id: 'holidays.list',
        method: 'GET',
        path: `${API_BASE}/holiday-calendars`,
        summary: 'Holiday calendars (everyone reads published ones; HR also sees drafts)',
        access: { auth: 'session', note: 'Any signed-in user; drafts only for HR.' },
        query: ListCalendarsQuery,
        success: { status: 200, description: 'Calendars, newest year first', schema: HolidayCalendarList },
        errors: ['no_session', 'validation_failed'],
        idempotency: 'safe',
        rateLimit: 'default',
    }),
    'holidays.get': defineRoute({
        id: 'holidays.get',
        method: 'GET',
        path: `${API_BASE}/holiday-calendars/{id}`,
        summary: 'A calendar with its days',
        access: { auth: 'session', note: 'Any signed-in user for published; a draft is 404 except for HR.' },
        params: CalendarIdParams,
        success: { status: 200, description: 'The calendar with days', schema: HolidayCalendar },
        errors: ['no_session', 'not_found'],
        idempotency: 'safe',
        rateLimit: 'default',
    }),
    'holidays.create': defineRoute({
        id: 'holidays.create',
        method: 'POST',
        path: `${API_BASE}/holiday-calendars`,
        summary: 'Start a draft calendar for a year (optionally copying another)',
        description: '409 when a draft already exists for that year and region.',
        access: { auth: 'session', anyOf: HR, note: 'HR. Others: 403.' },
        headers: IdempotencyHeaders,
        body: CreateCalendarBody,
        success: { status: 201, description: 'The draft', schema: HolidayCalendar },
        errors: ['no_session', 'forbidden', 'not_found', 'validation_failed', 'conflict'],
        idempotency: 'key',
        rateLimit: 'write',
    }),
    'holidays.update': defineRoute({
        id: 'holidays.update',
        method: 'PATCH',
        path: `${API_BASE}/holiday-calendars/{id}`,
        summary: 'Edit a draft (allowance, note)',
        access: { auth: 'session', anyOf: HR, note: 'HR, drafts only (409 when published). Others: 404.' },
        params: CalendarIdParams,
        headers: IfMatchHeaders,
        body: UpdateCalendarBody,
        success: { status: 200, description: 'Updated', schema: HolidayCalendar },
        errors: ['no_session', 'not_found', 'validation_failed', 'conflict', 'precondition_failed'],
        idempotency: 'safe',
        concurrency: 'if-match',
        rateLimit: 'write',
    }),
    'holidays.addDay': defineRoute({
        id: 'holidays.addDay',
        method: 'POST',
        path: `${API_BASE}/holiday-calendars/{id}/days`,
        summary: 'Add a day to a draft',
        description: '422 when the date is outside the calendar year or the day type is unknown/inactive; 409 when the date is already listed or the calendar is published.',
        access: { auth: 'session', anyOf: HR, note: 'HR, drafts only. Others: 404.' },
        params: CalendarIdParams,
        headers: IdempotencyHeaders,
        body: HolidayDayBody,
        success: { status: 201, description: 'The day', schema: HolidayDay },
        errors: ['no_session', 'not_found', 'validation_failed', 'conflict'],
        idempotency: 'key',
        rateLimit: 'write',
    }),
    'holidays.updateDay': defineRoute({
        id: 'holidays.updateDay',
        method: 'PATCH',
        path: `${API_BASE}/holiday-calendars/{id}/days/{day_id}`,
        summary: 'Edit a day on a draft',
        access: { auth: 'session', anyOf: HR, note: 'HR, drafts only. Others: 404.' },
        params: CalendarDayParams,
        body: UpdateHolidayDayBody,
        success: { status: 200, description: 'The day (holiday days are not versioned; last write wins, audited)', schema: HolidayDay },
        errors: ['no_session', 'not_found', 'validation_failed', 'conflict'],
        idempotency: 'safe',
        rateLimit: 'write',
    }),
    'holidays.deleteDay': defineRoute({
        id: 'holidays.deleteDay',
        method: 'DELETE',
        path: `${API_BASE}/holiday-calendars/{id}/days/{day_id}`,
        summary: 'Remove a day from a draft',
        access: { auth: 'session', anyOf: HR, note: 'HR, drafts only. Others: 404.' },
        params: CalendarDayParams,
        success: { status: 204, description: 'Removed' },
        errors: ['no_session', 'not_found', 'conflict'],
        idempotency: 'safe',
        rateLimit: 'write',
    }),
    'holidays.publish': defineRoute({
        id: 'holidays.publish',
        method: 'POST',
        path: `${API_BASE}/holiday-calendars/{id}/publish`,
        summary: 'Publish a draft (supersedes the live calendar for that year; recomputes working-day maths)',
        access: { auth: 'session', anyOf: HR, note: 'HR. Others: 404.' },
        params: CalendarIdParams,
        headers: IdempotencyHeaders,
        success: { status: 200, description: 'Published, with what changed', schema: PublishResponse },
        errors: ['no_session', 'not_found', 'validation_failed', 'conflict'],
        idempotency: 'key',
        rateLimit: 'write',
    }),
    'holidays.publishPreview': defineRoute({
        id: 'holidays.publishPreview',
        method: 'GET',
        path: `${API_BASE}/holiday-calendars/{id}/publish-preview`,
        summary: 'Preview publishing: what would change, and what would block it — changes nothing',
        access: { auth: 'session', anyOf: HR, note: 'HR. Others: 404.' },
        params: CalendarIdParams,
        success: { status: 200, description: 'The preview', schema: PublishPreview },
        errors: ['no_session', 'not_found', 'conflict'],
        idempotency: 'safe',
        rateLimit: 'default',
    }),
    'holidayTypes.list': defineRoute({
        id: 'holidayTypes.list',
        method: 'GET',
        path: `${API_BASE}/holiday-day-types`,
        summary: 'Holiday day types (fixed, optional, half day, …)',
        access: { auth: 'session', note: 'Any signed-in user.' },
        success: { status: 200, description: 'Day types', schema: HolidayDayTypeList },
        errors: ['no_session'],
        idempotency: 'safe',
        rateLimit: 'default',
    }),
    'holidayTypes.create': defineRoute({
        id: 'holidayTypes.create',
        method: 'POST',
        path: `${API_BASE}/holiday-day-types`,
        summary: 'Add a day type',
        access: { auth: 'session', anyOf: HR, note: 'HR. Others: 403.' },
        headers: IdempotencyHeaders,
        body: CreateDayTypeBody,
        success: { status: 201, description: 'The day type', schema: HolidayDayType },
        errors: ['no_session', 'forbidden', 'validation_failed', 'conflict'],
        idempotency: 'key',
        rateLimit: 'write',
    }),
    'holidayTypes.update': defineRoute({
        id: 'holidayTypes.update',
        method: 'PATCH',
        path: `${API_BASE}/holiday-day-types/{key}`,
        summary: 'Edit or deactivate a day type',
        access: { auth: 'session', anyOf: HR, deny: 'forbidden', note: 'HR. Others: 403.' },
        params: DayTypeKeyParams,
        body: UpdateDayTypeBody,
        success: { status: 200, description: 'The day type', schema: HolidayDayType },
        errors: ['no_session', 'forbidden', 'not_found', 'validation_failed'],
        idempotency: 'safe',
        rateLimit: 'write',
    }),
    // ============================================================ optional holidays
    'me.optionalHolidays': defineRoute({
        id: 'me.optionalHolidays',
        method: 'GET',
        path: `${API_BASE}/me/optional-holidays`,
        summary: 'Optional holidays I can take, what I elected, and what is left of my allowance',
        access: { auth: 'session', note: 'Any signed-in user; their own.' },
        query: MyOptionalQuery,
        success: { status: 200, description: 'Available and elected days', schema: MyOptionalHolidays },
        errors: ['no_session', 'validation_failed'],
        idempotency: 'safe',
        rateLimit: 'default',
    }),
    'me.optionalHolidaysElect': defineRoute({
        id: 'me.optionalHolidaysElect',
        method: 'POST',
        path: `${API_BASE}/me/optional-holidays`,
        summary: 'Take an optional holiday',
        description: '422 past the allowance or outside the timing window; 409 when already elected, or when its calendar was republished while this was in flight.',
        access: { auth: 'session', note: 'Any signed-in user; for themselves.' },
        headers: IdempotencyHeaders,
        body: ElectBody,
        success: { status: 201, description: 'The election', schema: OptionalElection },
        errors: ['no_session', 'not_found', 'validation_failed', 'conflict'],
        idempotency: 'key',
        rateLimit: 'write',
    }),
    'me.optionalHolidaysWithdraw': defineRoute({
        id: 'me.optionalHolidaysWithdraw',
        method: 'DELETE',
        path: `${API_BASE}/me/optional-holidays/{id}`,
        summary: 'Withdraw an optional holiday (may make a past day expected again)',
        description: '422 outside the timing window (then HR or a manager can).',
        access: { auth: 'session', note: 'Any signed-in user; their own (others: 404).' },
        params: ElectionIdParams,
        success: { status: 200, description: 'Withdrawn', schema: WithdrawResponse },
        errors: ['no_session', 'not_found', 'validation_failed'],
        idempotency: 'safe',
        rateLimit: 'write',
    }),
    'optionalHolidays.admin': defineRoute({
        id: 'optionalHolidays.admin',
        method: 'GET',
        path: `${API_BASE}/optional-holidays`,
        summary: 'Per person: elected optional days, used and remaining (the allowance view)',
        access: { auth: 'session', anyOf: OPTIONAL_ADMIN, note: 'HR and managers. Others: 403.' },
        query: MyOptionalQuery,
        success: { status: 200, description: 'One row per active person', schema: OptionalAdminList },
        errors: ['no_session', 'forbidden', 'validation_failed'],
        idempotency: 'safe',
        rateLimit: 'default',
    }),
    'optionalHolidays.electFor': defineRoute({
        id: 'optionalHolidays.electFor',
        method: 'POST',
        path: `${API_BASE}/users/{id}/optional-holidays`,
        summary: 'Elect an optional holiday on someone’s behalf (elected_by recorded)',
        access: { auth: 'session', anyOf: OPTIONAL_ADMIN, deny: 'forbidden', note: 'HR and managers. Others: 403.' },
        params: UserIdOnlyParams,
        headers: IdempotencyHeaders,
        body: ElectBody,
        success: { status: 201, description: 'The election', schema: OptionalElection },
        errors: ['no_session', 'forbidden', 'not_found', 'validation_failed', 'conflict'],
        idempotency: 'key',
        rateLimit: 'write',
    }),
    'optionalHolidays.withdrawFor': defineRoute({
        id: 'optionalHolidays.withdrawFor',
        method: 'DELETE',
        path: `${API_BASE}/users/{id}/optional-holidays/{election_id}`,
        summary: 'Withdraw someone’s optional holiday (after the timing window, only HR or a manager can)',
        access: { auth: 'session', anyOf: OPTIONAL_ADMIN, note: 'HR and managers. Others: 404.' },
        params: UserElectionParams,
        success: { status: 200, description: 'Withdrawn', schema: WithdrawResponse },
        errors: ['no_session', 'not_found'],
        idempotency: 'safe',
        rateLimit: 'write',
    }),
    'workingDays.get': defineRoute({
        id: 'workingDays.get',
        method: 'GET',
        path: `${API_BASE}/working-days`,
        summary: 'Per-user working-day calendar with expected hours per date (the UI never recomputes this)',
        access: { auth: 'session', note: 'Self; managers and HR for anyone (others asking for someone else: 404).' },
        query: WorkingDaysQuery,
        success: { status: 200, description: 'Days in range', schema: WorkingDaysResponse },
        errors: ['no_session', 'not_found', 'validation_failed'],
        idempotency: 'safe',
        rateLimit: 'default',
    }),
};
//# sourceMappingURL=routes.step4.js.map